import { PageClassSelector } from '../../../components/layout/TeacherWorkspace';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { lessonPreparationApi } from '../../../services/apiV2';
import { preferredTeacherSection, rememberTeacherSection } from '../../../services/teacherSection';

type Reflection = {
  what_happened?: string;
  missing_evidence?: string;
  disruptions?: string;
  needs_another_pass?: string;
  students_do_next?: string;
  teacher_interpretation?: string;
  class_strengths?: string;
  available_supports_resources?: string;
};

const errorMessage = (error: any): string => (
  error?.response?.data?.detail || 'Something went wrong. Please try again.'
);

const evidenceSourceHref = (ref: any, preparation: any): string => (
  ref.assignment_id && ref.student_id
    ? `/v2/teacher/assignments/${ref.assignment_id}/students/${ref.student_id}`
    : `/v2/teacher/run/${ref.lesson_run_id || preparation.source_run?.id}?step=${ref.step_id || ''}&student=${ref.student_id || ''}`
);

export const V2LessonPreparation: React.FC = () => {
  const [catalog, setCatalog] = useState<any>({ sections: [], eligible_runs: [], eligible_assignments: [], preparations: [] });
  const [sectionId, setSectionId] = useState<number | ''>('');
  useEffect(() => { if (sectionId) rememberTeacherSection(sectionId); }, [sectionId]);
  const [sourceKey, setSourceKey] = useState('');
  const [preparation, setPreparation] = useState<any>(null);
  const [reflection, setReflection] = useState<Reflection>({});
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [duration, setDuration] = useState(55);
  const [nextMeetingDate, setNextMeetingDate] = useState('');
  const [includedPreparationIds, setIncludedPreparationIds] = useState<number[]>([]);
  const [planningBrief, setPlanningBrief] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadCatalog = async () => {
    setLoading(true);
    try {
      const data = await lessonPreparationApi.list();
      setCatalog(data);
      if (!sectionId && data.sections?.length) setSectionId(preferredTeacherSection(data.sections) || '');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCatalog(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const eligibleRuns = useMemo(() => (
    (catalog.eligible_runs || []).filter((row: any) => !sectionId || row.section_id === sectionId)
  ), [catalog.eligible_runs, sectionId]);
  const eligibleAssignments = useMemo(() => (
    (catalog.eligible_assignments || []).filter((row: any) => !sectionId || row.section_id === sectionId)
  ), [catalog.eligible_assignments, sectionId]);

  const adoptPreparation = (row: any) => {
    setPreparation(row);
    setCatalog((current: any) => ({
      ...current,
      preparations: [row, ...(current.preparations || []).filter((item: any) => item.id !== row.id)],
    }));
    setReflection(row.reflection || {});
    setDismissed(row.dismissed_finding_ids || []);
    setNextMeetingDate(row.generation_metadata?.next_meeting_date || row.evidence_snapshot?.next_meeting_date || '');
    setIncludedPreparationIds([]);
    setPlanningBrief('');
  };

  const analyze = async () => {
    if (!sourceKey) return;
    setBusy('analyze'); setError(''); setNotice('');
    try {
      const [sourceType, rawId] = sourceKey.split(':');
      const sourceId = Number(rawId);
      const selected = sourceType === 'assignment'
        ? eligibleAssignments.find((row: any) => row.id === sourceId)
        : eligibleRuns.find((row: any) => row.id === sourceId);
      const row = selected?.preparation_id
        ? await lessonPreparationApi.get(selected.preparation_id)
        : await lessonPreparationApi.analyze(sourceType === 'assignment' ? { assignment_id: sourceId } : { run_id: sourceId });
      adoptPreparation(row);
    } catch (err) {
      setError(errorMessage(err));
    } finally { setBusy(''); }
  };

  const saveReflection = async () => {
    setBusy('reflection'); setError(''); setNotice('');
    try {
      const row = await lessonPreparationApi.saveReflection(preparation.id, reflection, dismissed);
      adoptPreparation(row);
      setNotice('Reflection saved separately from the automated analysis.');
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(''); }
  };

  const refresh = async () => {
    setBusy('refresh'); setError('');
    try {
      const row = await lessonPreparationApi.refresh(preparation.id);
      adoptPreparation(row);
      setNotice('Evidence refreshed. Your reflection was preserved.');
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(''); }
  };

  const buildPlanningBrief = async () => {
    setBusy('brief'); setError(''); setNotice('');
    try {
      // Building from the current form should never require a separate Save
      // click. Persist first so the backend brief reads exactly what is shown.
      const saved = await lessonPreparationApi.saveReflection(preparation.id, reflection, dismissed);
      setPreparation(saved);
      setReflection(saved.reflection || {});
      setCatalog((current: any) => ({
        ...current,
        preparations: [saved, ...(current.preparations || []).filter((item: any) => item.id !== saved.id)],
      }));
      const result = await lessonPreparationApi.planningBrief(saved.id, {
        duration_minutes: duration,
        next_meeting_date: nextMeetingDate || undefined,
        included_preparation_ids: includedPreparationIds,
      });
      setPlanningBrief(result.brief || '');
      try {
        await navigator.clipboard.writeText(result.brief || '');
        setNotice(`Planning brief copied for ChatGPT. No AI request was made by Elevate. ${result.privacy?.section_count || 1} section${result.privacy?.section_count === 1 ? '' : 's'} included.`);
      } catch {
        setNotice('Planning brief is ready below. Copy it manually; no AI request was made by Elevate.');
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally { setBusy(''); }
  };

  const toggleDismissed = (findingId: string) => setDismissed((current) => (
    current.includes(findingId) ? current.filter((value) => value !== findingId) : [...current, findingId]
  ));
  const sourceTitle = preparation?.source_run?.lesson_title || preparation?.source_assignment?.lesson_title || 'Evidence source';
  const sourceKind = preparation?.source_type === 'student_assignment' ? 'Assigned student work' : 'Teacher-led Runner';
  const sourcePlanId = preparation?.source_run?.lesson_plan_id || preparation?.source_assignment?.lesson_plan_id;
  const matchingPreparations = useMemo(() => (
    (catalog.preparations || []).filter((row: any) => (
      row.id !== preparation?.id
      && row.section?.id !== preparation?.section?.id
      && (row.source_run?.lesson_plan_id || row.source_assignment?.lesson_plan_id) === sourcePlanId
    ))
  ), [catalog.preparations, preparation, sourcePlanId]);

  if (loading) return <AppShell role="teacher" title="Review & Prepare" navItems={teacherV2NavItems('prepare')}><p>Loading…</p></AppShell>;

  return (
    <AppShell role="teacher" title="Review Today → Prepare Next Lesson" navItems={teacherV2NavItems('prepare')}>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold">What do these students need next?</h2>
          <Link className="inline-block my-3 underline" to="/v2/teacher/unit-day-review">Review a mixed day of live lessons and independent unit work</Link>
          <p className="mt-1 text-sm text-muted">Review evidence from a teacher-led Runner or assigned student work, add the classroom context only you know, then copy a planning brief into ChatGPT.</p>
        </div>

        {error && <div className="rounded-md border border-danger/30 bg-danger/10 p-3 text-sm text-danger">{error}</div>}
        {notice && <div className="rounded-md border border-success/30 bg-success/10 p-3 text-sm text-success">{notice}</div>}

        {!preparation ? (
          <Card>
            <CardHeader><CardTitle>1. Select the class and evidence source</CardTitle></CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-[1fr_2fr_auto] md:items-end">
              <PageClassSelector><label className="text-sm font-medium">Class
                <select className="mt-1 block h-10 w-full rounded-md border border-border bg-surface px-3" value={sectionId} onChange={(event) => { setSectionId(Number(event.target.value)); setSourceKey(''); }}>
                  {(catalog.sections || []).map((row: any) => <option key={row.id} value={row.id}>{row.name}</option>)}
                </select>
              </label></PageClassSelector>
              <label className="text-sm font-medium">Evidence source
                <select className="mt-1 block h-10 w-full rounded-md border border-border bg-surface px-3" value={sourceKey} onChange={(event) => setSourceKey(event.target.value)}>
                  <option value="">Choose a Runner or assignment</option>
                  <optgroup label="Teacher-led Runners">
                    {eligibleRuns.map((row: any) => <option key={`run-${row.id}`} value={`run:${row.id}`}>{row.lesson_title} · {new Date(row.started_at).toLocaleDateString()}{row.preparation_id ? ' · review started' : ''}</option>)}
                  </optgroup>
                  <optgroup label="Assigned student work">
                    {eligibleAssignments.map((row: any) => <option key={`assignment-${row.id}`} value={`assignment:${row.id}`}>{row.lesson_title} · {new Date(row.assigned_at).toLocaleDateString()} · {row.completed_count}/{row.assigned_count} complete{!row.is_active ? ' · closed' : ''}{row.preparation_id ? ' · review started' : ''}</option>)}
                  </optgroup>
                </select>
              </label>
              <Button onClick={analyze} disabled={!sourceKey} loading={busy === 'analyze'}>Analyze evidence</Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div><Badge>{preparation.status}</Badge><span className="ml-2 text-sm text-muted">{preparation.section.name} · {sourceTitle} · {sourceKind}</span></div>
              <Button variant="secondary" onClick={() => setPreparation(null)}>Choose another source</Button>
            </div>

            {preparation.is_stale && <div className="rounded-md border border-warning/40 bg-warning/10 p-4 text-sm"><strong>New or changed submissions are available.</strong> Refreshing recalculates the evidence snapshot without removing your reflection. <Button className="ml-3" size="sm" variant="secondary" onClick={refresh} loading={busy === 'refresh'}>Refresh evidence</Button></div>}

            <div className="grid gap-4 md:grid-cols-5">
              {Object.entries(preparation.evidence_snapshot.completion || {}).filter(([key]) => ['assigned', 'started', 'submitted', 'completed', 'incomplete'].includes(key)).map(([key, value]) => (
                <Card key={key}><CardContent className="pt-5"><p className="text-xs uppercase tracking-wide text-muted">{key}</p><p className="mt-1 text-3xl font-semibold">{String(value)}</p></CardContent></Card>
              ))}
            </div>
            <p className="text-xs text-muted">Denominator: {preparation.evidence_snapshot.denominator?.definition} Analysis time: {new Date(preparation.analyzed_at).toLocaleString()}.</p>
            {(preparation.source_type === 'student_assignment' || (preparation.evidence_snapshot.delivery_contexts || []).length > 1 || (preparation.evidence_snapshot.lesson_runs || []).length > 1) && <Card><CardHeader><CardTitle>{preparation.source_type === 'student_assignment' ? 'Assignment evidence context' : 'Delivery contexts kept separate'}</CardTitle></CardHeader><CardContent className="grid gap-3 md:grid-cols-2">{preparation.evidence_snapshot.delivery_contexts.map((context: any, index: number) => <div key={`${context.type}-${context.assignment_id || index}`} className="rounded-md border border-border p-3"><p className="font-medium">{context.label}{context.delivery_mode ? ` · ${context.delivery_mode.replace(/_/g, ' ')}` : ''}</p><p className="mt-1 text-sm text-muted">{context.counts.completed} of {context.counts.assigned} complete · {context.counts.submitted} submitted</p><p className="mt-1 text-xs text-muted">{context.attempt_policy}</p>{context.assignment_id && <Link className="mt-2 inline-block text-xs underline" to={`/v2/teacher/assignments/${context.assignment_id}/summary`}>Open assignment evidence</Link>}</div>)}</CardContent></Card>}

            <Card>
              <CardHeader><CardTitle>Project Hook & timeline guardrails</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p><strong>{preparation.evidence_snapshot.project_alignment?.hook_title}</strong>{preparation.evidence_snapshot.project_alignment?.standard_code ? ` · ${preparation.evidence_snapshot.project_alignment.standard_code}` : ''}</p>
                <p className="text-muted">This lesson is {preparation.evidence_snapshot.project_alignment?.source_position || 'an unnumbered'} of {preparation.evidence_snapshot.project_alignment?.lesson_count || 'the current'} project lessons.</p>
                {preparation.evidence_snapshot.project_alignment?.next_planned_lesson ? <p>Planned next: <strong>{preparation.evidence_snapshot.project_alignment.next_planned_lesson.title}</strong>{preparation.evidence_snapshot.project_alignment.next_planned_lesson.planned_date ? ` on ${preparation.evidence_snapshot.project_alignment.next_planned_lesson.planned_date}` : ''}. The draft adapts this progression; it does not replace the Hook’s outcomes.</p> : <p className="text-warning">No next project lesson is scheduled. The draft will remain aligned to the Hook, but confirm its place in the timeline before approval.</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>2. Evidence-supported findings</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p>{preparation.analysis.summary}</p>
                {(preparation.analysis.findings || []).map((finding: any) => (
                  <div key={finding.id} className={`rounded-md border p-3 ${dismissed.includes(finding.id) ? 'opacity-50' : ''}`}>
                    <div className="flex items-start justify-between gap-3"><p className="text-sm">{finding.statement}</p><Badge variant={finding.observed_fact ? 'default' : 'warning'}>{finding.observed_fact ? 'Observed' : 'Hypothesis'}</Badge></div>
                    <p className="mt-2 text-xs text-muted">Sources: {(finding.evidence_refs || []).length ? (finding.evidence_refs || []).slice(0, 8).map((ref: any, index: number) => <React.Fragment key={`${ref.kind}-${ref.id}-${index}`}><Link className="underline" to={evidenceSourceHref(ref, preparation)}>{ref.kind} #{ref.id}{ref.attempt_number ? ` attempt ${ref.attempt_number}` : ''}</Link>{index < Math.min((finding.evidence_refs || []).length, 8) - 1 ? ', ' : ''}</React.Fragment>) : `${sourceKind} completion audience`}{(finding.evidence_refs || []).length > 8 ? '…' : ''}</p>
                    <label className="mt-3 flex items-center gap-2 text-xs"><Checkbox checked={dismissed.includes(finding.id)} onChange={() => toggleDismissed(finding.id)} /> Dismiss this inference (source evidence stays attached)</label>
                  </div>
                ))}
                <div><h3 className="text-sm font-semibold">Proposed adjustments</h3><ul className="mt-2 list-disc space-y-1 pl-5 text-sm">{(preparation.analysis.proposed_adjustments || []).map((row: any, index: number) => <li key={index}>{row.text}</li>)}</ul></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>3. Add your reflection</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {(preparation.analysis.reflection_questions || []).map((row: any) => <p key={row.id} className="rounded-md bg-surface-alt p-3 text-sm">{row.prompt}</p>)}
                <label className="block text-sm font-medium">What happened in class?<Textarea className="mt-1" rows={2} value={reflection.what_happened || ''} onChange={(event) => setReflection({ ...reflection, what_happened: event.target.value })} /></label>
                <label className="block text-sm font-medium">Verbal, hands-on, or other missing evidence<Textarea className="mt-1" rows={2} value={reflection.missing_evidence || ''} onChange={(event) => setReflection({ ...reflection, missing_evidence: event.target.value })} /></label>
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="block text-sm font-medium">Current class strengths you observed<Textarea className="mt-1" rows={2} value={reflection.class_strengths || ''} onChange={(event) => setReflection({ ...reflection, class_strengths: event.target.value })} /></label>
                  <label className="block text-sm font-medium">Available supports, accommodations, equipment, or resources<Textarea className="mt-1" rows={2} value={reflection.available_supports_resources || ''} onChange={(event) => setReflection({ ...reflection, available_supports_resources: event.target.value })} /></label>
                </div>
                <label className="block text-sm font-medium">Time, attendance, technology, or classroom disruptions<Textarea className="mt-1" rows={2} value={reflection.disruptions || ''} onChange={(event) => setReflection({ ...reflection, disruptions: event.target.value })} /></label>
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="block text-sm font-medium">What needs another pass?<Textarea className="mt-1" rows={2} value={reflection.needs_another_pass || ''} onChange={(event) => setReflection({ ...reflection, needs_another_pass: event.target.value })} /></label>
                  <label className="block text-sm font-medium">What should students do next?<Textarea className="mt-1" rows={2} value={reflection.students_do_next || ''} onChange={(event) => setReflection({ ...reflection, students_do_next: event.target.value })} /></label>
                </div>
                <Button onClick={saveReflection} loading={busy === 'reflection'}>Save reflection</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>4. Create a ChatGPT planning brief</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted">Elevate prepares a privacy-minimized handoff from aggregate evidence, your reflection, and the project timeline. It does not call an AI model or create a lesson.</p>
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="text-sm font-medium">Available class time (minutes)<Input className="mt-1" type="number" min={10} max={240} value={duration} onChange={(event) => setDuration(Number(event.target.value))} /></label>
                  <label className="text-sm font-medium">Next class meeting<Input className="mt-1" type="date" value={nextMeetingDate} onChange={(event) => setNextMeetingDate(event.target.value)} /></label>
                </div>
                {matchingPreparations.length > 0 && <div className="rounded-md border border-border p-3"><p className="text-sm font-medium">Combine matching sections/groups</p><p className="mt-1 text-xs text-muted">Only reviews of the same Lesson Plan can be combined. Counts are totaled, while each section’s findings and reflection remain labeled.</p><div className="mt-3 grid gap-2 md:grid-cols-2">{matchingPreparations.map((row: any) => <label key={row.id} className="flex items-center gap-2 text-sm"><Checkbox checked={includedPreparationIds.includes(row.id)} onChange={() => setIncludedPreparationIds((current) => current.includes(row.id) ? current.filter((id) => id !== row.id) : [...current, row.id])} /> {row.section.name} · {row.source_type === 'student_assignment' ? 'assignment' : 'Runner'}</label>)}</div></div>}
                {!Object.keys(preparation.reflection || {}).length && <p className="text-xs text-warning">No teacher reflection is saved for this section. The brief will tell ChatGPT to keep recommendations provisional.</p>}
                <Button onClick={buildPlanningBrief} loading={busy === 'brief'}>Build & copy planning brief</Button>
                {planningBrief && <div><label className="text-sm font-medium">Planning brief<Textarea className="mt-1 font-mono text-xs" rows={24} readOnly value={planningBrief} /></label><p className="mt-2 text-xs text-muted">Review before pasting. Known enrolled-student identities and contact information are redacted, and response-level records are excluded.</p></div>}
                {preparation.draft_lesson_plan_id && <p className="text-xs text-muted">A draft was created previously. <Link className="underline" to={`/v2/teacher/lesson-plan/${preparation.draft_lesson_plan_id}/edit`}>Open the existing draft</Link>. New in-app generation is disabled by default.</p>}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AppShell>
  );
};
